import Fuse from 'fuse.js';
import { useMemo } from 'react';
import { useDebouncedValue } from 'v2/hooks/useDebouncedValue/useDebouncedValue';

const SEARCH_DEBOUNCE_MS = 300;
const FUSE_THRESHOLD = 0.3;

type Args<T> = {
  /**
   * Should be memoized by the caller if derived from other data,
   * otherwise the Fuse index will be rebuilt on each render.
   */
  data: T[];
  search: string;
  /**
   * Should be a stable array, preferably defined outside the component.
   */
  keys: string[];
};

/**
 * Performs a fuzzy search using Fuse.js.
 *
 * Substring matches are preferred over fuzzy matches. If one or more results
 * contain the search string as a contiguous substring, only those are returned.
 * This keeps the search deterministic, which is also useful for testing.
 */
export function useSearch<T>({ data, search, keys }: Args<T>): T[] {
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const fuse = useMemo(() => {
    return new Fuse(data, {
      keys,
      threshold: FUSE_THRESHOLD,
      includeScore: true,
      includeMatches: true,
      // match quality shouldn't depend on where in the string the match falls.
      ignoreLocation: true,
      // without it, Fuse penalizes fuzzy matches more in longer strings.
      ignoreFieldNorm: true
    });
  }, [data, keys]);

  const trimmedSearch = debouncedSearch.trim().toLowerCase();

  return useMemo(() => {
    if (!trimmedSearch) return data;

    const results = fuse.search(trimmedSearch);

    const substringMatches = results.filter((result) =>
      result.matches?.some((match) => match.value?.trim().toLowerCase().includes(trimmedSearch))
    );

    return substringMatches.length > 0
      ? substringMatches.map((result) => result.item)
      : results
          .filter((result) => result.score !== undefined && result.score <= FUSE_THRESHOLD)
          .map((result) => result.item);
  }, [data, fuse, trimmedSearch]);
}
